import { createBrowserRouter } from 'react-router';

import StorefrontLayout from './components/layout/StorefrontLayout.jsx';
import AdminLayout from './components/layout/AdminLayout.jsx';
import RequireAuth from './components/auth/RequireAuth.jsx';
import RequireAdmin from './components/auth/RequireAdmin.jsx';
import RouteErrorPage from './pages/RouteErrorPage.jsx';
import NotFoundPage from './pages/NotFoundPage.jsx';
import HomePage from './pages/home/HomePage.jsx';

/**
 * The whole route map, in one file, on purpose: it is the clearest single
 * statement of what this application is, and it is the thing to review before
 * any of the pages behind it get built.
 *
 * Every page except the home page is code-split. `lazy` returns a route module,
 * so React Router holds the navigation until the chunk lands — no Suspense
 * boundary, no layout shift, no flash of a spinner.
 */
const page = (load) => () => load().then((module) => ({ Component: module.default }));

/**
 * Wraps a group of routes in a pathless layout guard. The guard renders its
 * pending line until the session bootstrap in `App.jsx` resolves, so a reload on
 * /account does not bounce a signed-in reader to /login.
 *
 * These are redirects, not security. Every endpoint underneath re-checks the
 * token and the role server-side.
 */
function protect(Guard, children) {
  return [{ Component: Guard, children }];
}

/**
 * The route tree is exported separately from the browser router so tests can
 * mount it under a memory router without touching `window.history`.
 */
export const routes = [
  /* ===================================================================== *
   * Storefront
   * ===================================================================== */
  {
    path: '/',
    Component: StorefrontLayout,
    ErrorBoundary: RouteErrorPage,
    children: [
      { index: true, Component: HomePage },

      /* --- Publisher catalog. Separate route, separate query, separate
             collection from secondhand. The two never share a list page. --- */
      { path: 'new', lazy: page(() => import('./pages/catalog/PublisherCatalogPage.jsx')) },
      { path: 'new/:slug', lazy: page(() => import('./pages/book/PublisherBookPage.jsx')) },

      /* --- Secondhand catalog. Same shape, deliberately duplicated. ------ */
      { path: 'secondhand', lazy: page(() => import('./pages/catalog/SecondhandCatalogPage.jsx')) },
      {
        path: 'secondhand/:slug',
        lazy: page(() => import('./pages/book/SecondhandBookPage.jsx')),
      },

      { path: 'search', lazy: page(() => import('./pages/static/SearchPage.jsx')) },
      { path: 'subscription', lazy: page(() => import('./pages/static/SubscriptionPage.jsx')) },
      { path: 'cart', lazy: page(() => import('./pages/cart/CartPage.jsx')) },

      /* --- Editorial / static ------------------------------------------- */
      { path: 'about', lazy: page(() => import('./pages/static/AboutPage.jsx')) },
      { path: 'contact', lazy: page(() => import('./pages/static/ContactPage.jsx')) },
      { path: 'book-fair', lazy: page(() => import('./pages/static/BookFairPage.jsx')) },
      { path: 'policies/:slug', lazy: page(() => import('./pages/static/PolicyPage.jsx')) },

      /* --- Auth. Inside the storefront shell: signing in is part of the
             shop, not a separate product. ------------------------------- */
      { path: 'login', lazy: page(() => import('./pages/auth/LoginPage.jsx')) },
      { path: 'signup', lazy: page(() => import('./pages/auth/SignupPage.jsx')) },
      { path: 'forgot-password', lazy: page(() => import('./pages/auth/ForgotPasswordPage.jsx')) },
      {
        path: 'reset-password/:token',
        lazy: page(() => import('./pages/auth/ResetPasswordPage.jsx')),
      },

      /* --- Signed in only ----------------------------------------------- */
      ...protect(RequireAuth, [
        { path: 'checkout', lazy: page(() => import('./pages/checkout/CheckoutPage.jsx')) },
        {
          path: 'checkout/complete/:orderId',
          lazy: page(() => import('./pages/checkout/ConfirmationPage.jsx')),
        },
        { path: 'account', lazy: page(() => import('./pages/account/AccountPage.jsx')) },
        { path: 'account/orders', lazy: page(() => import('./pages/account/OrdersPage.jsx')) },
        {
          path: 'account/orders/:orderId',
          lazy: page(() => import('./pages/account/OrderDetailPage.jsx')),
        },
      ]),

      { path: '*', Component: NotFoundPage },
    ],
  },

  /* ===================================================================== *
   * Admin. Same design language, different shell. Client-side guarding is a
   * convenience; every endpoint underneath re-checks the role server-side.
   * ===================================================================== */
  {
    path: '/admin',
    ErrorBoundary: RouteErrorPage,
    children: protect(RequireAdmin, [
      {
        Component: AdminLayout,
        children: [
          { index: true, lazy: page(() => import('./pages/admin/DashboardPage.jsx')) },

          /* --- Catalog: two separate sections, never one merged table. --- */
          {
            path: 'books/secondhand',
            lazy: page(() => import('./pages/admin/SecondhandBooksPage.jsx')),
          },
          {
            path: 'books/publisher',
            lazy: page(() => import('./pages/admin/PublisherBooksPage.jsx')),
          },
          { path: 'publishers', lazy: page(() => import('./pages/admin/PublishersPage.jsx')) },
          { path: 'categories', lazy: page(() => import('./pages/admin/CategoriesPage.jsx')) },

          /* --- Commerce -------------------------------------------------- */
          { path: 'orders', lazy: page(() => import('./pages/admin/OrdersPage.jsx')) },
          {
            path: 'orders/:orderId',
            lazy: page(() => import('./pages/admin/OrderDetailPage.jsx')),
          },
          {
            path: 'subscription-plan',
            lazy: page(() => import('./pages/admin/SubscriptionPlanPage.jsx')),
          },
          { path: 'promo-codes', lazy: page(() => import('./pages/admin/PromoCodesPage.jsx')) },
          { path: 'promotions', lazy: page(() => import('./pages/admin/PromotionsPage.jsx')) },

          /* --- People ---------------------------------------------------- */
          { path: 'users', lazy: page(() => import('./pages/admin/UsersPage.jsx')) },
          { path: 'users/:userId', lazy: page(() => import('./pages/admin/UserDetailPage.jsx')) },
          { path: 'audit-log', lazy: page(() => import('./pages/admin/AuditLogPage.jsx')) },

          { path: '*', Component: NotFoundPage },
        ],
      },
    ]),
  },
];

export const router = createBrowserRouter(routes);

export default router;
